// src/pages/Reviews.jsx
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import LocalizedLink from "../components/LocalizedLink";
import { createPortal } from "react-dom";

export default function ReviewsPage() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(null); // selected review or null

  const TT = (key, fallback) => {
    const v = t(key);
    return v === key ? fallback : v;
  };

  const items = t("reviews.items", { returnObjects: true }) || [];
  const safeStars = (n) => "★".repeat(Math.max(0, Math.min(5, Number(n) || 0)));
  const ratedQuestionLabel = (k) => t(`reviews.questions.rated.${k}`, k);
  const freeQuestionLabel = (k) => t(`reviews.questions.free.${k}`, k);

  // ESC to close + lock scroll when modal open
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(null);
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    if (open) document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <section className="mx-auto max-w-5xl px-4 sm:px-6 py-12 sm:py-16 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-100">
            {TT("reviews.band", "What clients say")}
            <span className="inline-block size-1 rounded-full bg-indigo-300" />
            {TT("reviews.tagline", "Trust built on results")}
          </span>
          <h1 className="mt-4 text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight break-words">
            {TT("reviews.heading", "Client Reviews")}
          </h1>
          <p className="mt-2 text-slate-600 break-words">
            {TT("reviews.subheading", "Real feedback from real projects.")}
          </p>
        </div>

        <div className="sm:shrink-0">
          <LocalizedLink
            to="/contact"
            className="w-full sm:w-auto inline-flex justify-center rounded-xl bg-indigo-600 px-4 sm:px-5 py-2.5 sm:py-3 text-white text-sm sm:text-base font-medium hover:bg-indigo-500"
          >
            {TT("reviews.cta", "Start your project")}
          </LocalizedLink>
        </div>
      </div>

      {/* Cards */}
      <div className="mt-8 sm:mt-10 grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((r, idx) => (
          <article
            key={idx}
            className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm flex flex-col min-w-0"
          >
            {/* Top row: logo + name */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                {r.logo ? (
                  <img
                    src={r.logo}
                    alt={r.company || r.name}
                    className="shrink-0 h-8 sm:h-10 w-auto max-w-[50vw] sm:max-w-[180px] object-contain"
                    loading="lazy"
                  />
                ) : null}
                <div className="min-w-0">
                  <h3 className="text-sm sm:text-base font-semibold text-slate-900 truncate">
                    {r.name}
                    {r.company ? (
                      <span className="text-slate-500 font-normal"> — {r.company}</span>
                    ) : null}
                  </h3>
                  {r.role ? (
                    <div className="text-[11px] sm:text-xs text-slate-500 truncate">{r.role}</div>
                  ) : null}
                </div>
              </div>

              {/* Overall Rating */}
              <div
                aria-label={t("reviews.aria_rating", { rating: r.rating })}
                className="text-xs sm:text-sm font-medium text-amber-600 whitespace-nowrap"
              >
                {safeStars(r.rating)}
              </div>
            </div>

            {/* Only the rating keys (compact) */}
            {Array.isArray(r.ratings) && r.ratings.length > 0 && (
              <div className="mt-3 sm:mt-4 space-y-1.5">
                {r.ratings.map((q) => (
                  <div
                    key={q.key}
                    className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-2.5 py-1.5 ring-1 ring-slate-200"
                  >
                    <div className="text-[12px] sm:text-sm text-slate-700 truncate">
                      {ratedQuestionLabel(q.key)}
                    </div>
                    <div className="shrink-0 text-[11px] sm:text-xs font-semibold text-slate-800">
                      {t("reviews.questions.labels.score_out_of", { score: q.score })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* CTA: More details -> opens modal */}
            <button
              type="button"
              onClick={() => setOpen(r)}
              className="mt-4 inline-flex justify-center rounded-xl bg-indigo-600 px-3 sm:px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60"
            >
              {TT("reviews.more_details", "More details")}
            </button>

            <div className="mt-2 text-[11px] sm:text-xs text-slate-500">{r.date}</div>
          </article>
        ))}
      </div>

      {/* Modal (portal) */}
      {open &&
  createPortal(
    <div
      className="fixed inset-0 z-[10000] bg-black/50 flex items-end sm:items-center justify-center overflow-x-hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Review details"
      onClick={() => setOpen(null)}
    >
      <div
        /* Full-screen on mobile; centered panel on >= sm */
        className="w-screen sm:w-full sm:max-w-3xl md:max-w-4xl bg-white rounded-none sm:rounded-2xl shadow-xl
                   h-[100dvh] sm:h-auto sm:max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header (fixed area) */}
        <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b border-slate-200">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              {open.logo ? (
                <img
                  src={open.logo}
                  alt={open.company || open.name}
                  className="shrink-0 h-10 sm:h-12 w-auto max-w-[60vw] sm:max-w-[200px] object-contain"
                  loading="lazy"
                />
              ) : null}
              <div className="min-w-0">
                <h3 className="text-base sm:text-lg font-semibold text-slate-900 truncate">
                  {open.name}
                  {open.company ? (
                    <span className="text-slate-500 font-normal"> — {open.company}</span>
                  ) : null}
                </h3>
                {open.role ? (
                  <div className="text-xs text-slate-500 truncate">{open.role}</div>
                ) : null}
                <div className="mt-1 text-[11px] text-slate-500">{open.date}</div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div
                aria-label={t("reviews.aria_rating", { rating: open.rating })}
                className="text-sm font-medium text-amber-600"
              >
                {"★".repeat(Math.max(0, Math.min(5, Number(open.rating) || 0)))}
              </div>
              <button
                type="button"
                onClick={() => setOpen(null)}
                className="ml-2 inline-flex items-center justify-center rounded-full p-2 text-slate-500 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500/60"
                aria-label={TT("reviews.close", "Close")}
              >
                <svg viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 011.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable content (only this part scrolls) */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
          {/* Rated questions */}
          {Array.isArray(open.ratings) && open.ratings.length > 0 && (
            <div className="grid gap-2">
              {open.ratings.map((q) => (
                <div
                  key={q.key}
                  className="flex items-start justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2 ring-1 ring-slate-200"
                >
                  <div className="text-sm text-slate-700 break-words">
                    {t(`reviews.questions.rated.${q.key}`, q.key)}
                  </div>
                  <div className="shrink-0 text-xs font-semibold text-slate-800">
                    {t("reviews.questions.labels.score_out_of", { score: q.score })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Free-text Q&A */}
          {open.answers && (
            <div className="mt-6 space-y-4">
              {Object.entries(open.answers).map(([k, val]) => (
                <div key={k} className="rounded-xl border border-slate-200 p-4">
                  <div className="text-[13px] font-semibold text-slate-600">
                    {t(`reviews.questions.free.${k}`, k)}
                  </div>
                  <p className="mt-1 text-slate-800 leading-relaxed whitespace-pre-line break-words">
                    {val}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer (fixed area) */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-200">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setOpen(null)}
              className="inline-flex justify-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500/60"
            >
              {TT("reviews.close", "Close")}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )}
    </section>
  );
}
